import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DateStamp from '@components/dates/DateStamp';
import styles from '@components/forms/careProfile/CareProfile.module.css';
import Nbsp from '@components/Nbsp';
import currencies from '@i18n/currencies';
import { CareProfileChangeFieldEnum, CareProfileChangeKindEnum, GetCareProfileChangesQuery } from '@typing/Generated';
import { languageNameForCode } from '@utils/languageUtils';
import { snakeToCamel } from '@utils/stringUtils';

type CareProfileChangeType = NonNullable<
  NonNullable<NonNullable<GetCareProfileChangesQuery['careProfileChanges']['edges']>[number]>['node']
>;

type CareProfileChangeResourceType = CareProfileChangeType['resource'];

type Props = {
  change: CareProfileChangeType;
};

type CareProfileChangeValueProps = {
  field: CareProfileChangeFieldEnum;
  resource: CareProfileChangeResourceType;
  value: any;
};

const CareProfileChangeModificationValue = ({ field, resource, value }: CareProfileChangeValueProps) => {
  if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
    return 'blank';
  }

  if (Array.isArray(value)) {
    const items = value.map((v, idx) => (
      <span key={v}>
        <CareProfileChangeModificationValue field={field} resource={resource} value={v} />
        {idx < value.length - 1 && (
          <>
            <FormattedMessage id="dictionary.punctuation.comma" />
            <Nbsp />
          </>
        )}
      </span>
    ));
    return items;
  }

  if (
    [
      CareProfileChangeFieldEnum.date,
      CareProfileChangeFieldEnum.date_of_death,
      CareProfileChangeFieldEnum.pregnancy_due_date,
      CareProfileChangeFieldEnum.update_date
    ].includes(field)
  ) {
    return <DateStamp absoluteDate value={new Date(value)} />;
  } else if (
    [
      CareProfileChangeFieldEnum.is_veteran,
      CareProfileChangeFieldEnum.is_veteran_guardian,
      CareProfileChangeFieldEnum.is_veteran_partner,
      CareProfileChangeFieldEnum.legal_help_needed,
      CareProfileChangeFieldEnum.legally_disabled,
      CareProfileChangeFieldEnum.recent_hospitalization,
      CareProfileChangeFieldEnum.va_connected
    ].includes(field)
  ) {
    return value ? <FormattedMessage id="dictionary.yes" /> : <FormattedMessage id="dictionary.no" />;
  } else if (
    [
      CareProfileChangeFieldEnum.stage_bathing_and_dressing,
      CareProfileChangeFieldEnum.stage_continence,
      CareProfileChangeFieldEnum.stage_eating,
      CareProfileChangeFieldEnum.stage_housekeeping,
      CareProfileChangeFieldEnum.stage_meal_prep,
      CareProfileChangeFieldEnum.stage_shopping,
      CareProfileChangeFieldEnum.stage_toileting,
      CareProfileChangeFieldEnum.stage_transportation
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.stageLimited.${value}`} />;
  } else if (
    [
      CareProfileChangeFieldEnum.ever_been_caregiver,
      CareProfileChangeFieldEnum.medications_affordable,
      CareProfileChangeFieldEnum.high_risk_pregnancy,
      CareProfileChangeFieldEnum.recent_incident,
      CareProfileChangeFieldEnum.day_to_day_support_needed,
      CareProfileChangeFieldEnum.life_threatening_condition,
      CareProfileChangeFieldEnum.long_term_condition
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.yesNoDontKnowOptions.${value}`} />;
  } else if (
    [
      CareProfileChangeFieldEnum.caregiving_impact_financial,
      CareProfileChangeFieldEnum.caregiving_impact_physical,
      CareProfileChangeFieldEnum.caregiving_impact_work
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.caregivingImpactOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.household_income_currency_code) {
    return currencies[value];
  } else if (field === CareProfileChangeFieldEnum.control) {
    return <FormattedMessage id={`models.diagnosis.controlOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.format) {
    return <FormattedMessage id={`models.medication.formatOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.guardian_employment_status) {
    return <FormattedMessage id={`models.careProfile.employmentStatusOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.guardian_marital_status) {
    return <FormattedMessage id={`models.careProfile.maritalStatusOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.kind && resource.__typename === 'LegalDocument') {
    return <FormattedMessage id={`models.legalDocument.kindOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.kind && resource.__typename === 'Medication') {
    return <FormattedMessage id={`models.medication.kindOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.kind && resource.__typename === 'Provider') {
    return <FormattedMessage id={`models.provider.kindOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.language_code) {
    return languageNameForCode(value);
  } else if (field === CareProfileChangeFieldEnum.pain_severity) {
    return <FormattedMessage id={`models.careProfile.severityOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.payment_type) {
    return <FormattedMessage id={`models.medication.paymentTypeOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.relationship) {
    return <FormattedMessage id={`models.clientRole.relationship.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.stage) {
    return <FormattedMessage id={`models.journey.stage.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.status) {
    return <FormattedMessage id={`models.legalDocument.statusOptions.${value}`} />;
  } else if (
    [
      CareProfileChangeFieldEnum.acuity,
      CareProfileChangeFieldEnum.age,
      CareProfileChangeFieldEnum.care_types_provided,
      CareProfileChangeFieldEnum.caregiver_types,
      CareProfileChangeFieldEnum.caregiving_duration,
      CareProfileChangeFieldEnum.caregiving_time_per_week,
      CareProfileChangeFieldEnum.cognitive_statuses,
      CareProfileChangeFieldEnum.conservatorship_status,
      CareProfileChangeFieldEnum.current_care_transitions,
      CareProfileChangeFieldEnum.developmental_or_educational_concerns,
      CareProfileChangeFieldEnum.distance_to_loved_one,
      CareProfileChangeFieldEnum.durable_medical_equipment,
      CareProfileChangeFieldEnum.emotional_concerns,
      CareProfileChangeFieldEnum.employment_status,
      CareProfileChangeFieldEnum.ever_been_caregiver,
      CareProfileChangeFieldEnum.fall_count,
      CareProfileChangeFieldEnum.financial_manager,
      CareProfileChangeFieldEnum.marital_status,
      CareProfileChangeFieldEnum.guardian_type,
      CareProfileChangeFieldEnum.health_insurance_types,
      CareProfileChangeFieldEnum.household_income_level,
      CareProfileChangeFieldEnum.immigration_status,
      CareProfileChangeFieldEnum.income_sources,
      CareProfileChangeFieldEnum.insurance_types,
      CareProfileChangeFieldEnum.medication_management_status,
      CareProfileChangeFieldEnum.pronouns,
      CareProfileChangeFieldEnum.provider_emotional_state,
      CareProfileChangeFieldEnum.provider_feels_supported,
      CareProfileChangeFieldEnum.provider_financial_concerns,
      CareProfileChangeFieldEnum.religion,
      CareProfileChangeFieldEnum.residence_type,
      CareProfileChangeFieldEnum.severity,
      CareProfileChangeFieldEnum.sense_of_fulfillment,
      CareProfileChangeFieldEnum.social_satisfaction,
      CareProfileChangeFieldEnum.stability_supports,
      CareProfileChangeFieldEnum.types_of_support_needed,
      CareProfileChangeFieldEnum.use_case
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.${snakeToCamel(field)}Options.${value}`} />;
  } else {
    return `${value}`;
  }
};

const CareProfileChange = ({ change }: Props) => {
  const intl = useIntl();

  const fieldDescription = useMemo(() => {
    if (change.kind === CareProfileChangeKindEnum.MODIFICATION) {
      const camel = snakeToCamel(change.field as string);

      if (change.resource.__typename === 'Client') {
        return (
          <>
            <FormattedMessage id={`forms.careProfileNew.${camel}Label`} />
            <Nbsp />
            <FormattedMessage id="models.careProfileChange.forName" values={{ name: change.resource.fullName }} />
          </>
        );
      } else if (change.resource.__typename === 'ClientRole') {
        return (
          <>
            <FormattedMessage id={`forms.careProfileNew.${camel}Label`} />
            <Nbsp />
            <FormattedMessage
              id="models.careProfileChange.forName"
              values={{ name: change.resource.client.fullName }}
            />
          </>
        );
      } else if (change.resource.__typename === 'Diagnosis') {
        const field = intl.formatMessage({ id: `forms.diagnosis.${camel}Label` });

        return (
          <FormattedMessage
            id="models.careProfileChange.diagnosisChangeDescription"
            values={{ condition: change.resource.condition, field }}
          />
        );
      } else if (change.resource.__typename === 'LegalDocument') {
        const field = intl.formatMessage({ id: `forms.legalDocument.${camel}Label` });

        const kind = intl.formatMessage({ id: `models.legalDocument.kindOptions.${change.resource.kind}` });

        return (
          <FormattedMessage id="models.careProfileChange.legalDocumentChangeDescription" values={{ field, kind }} />
        );
      } else if (change.resource.__typename === 'Location') {
        if (change.resource.locationCareProfile?.name) {
          return (
            <FormattedMessage
              id="models.careProfileChange.locationChangeDescription"
              values={{ name: change.resource.locationCareProfile.name }}
            />
          );
        } else if (change.resource.locationClient?.fullName) {
          return (
            <FormattedMessage
              id="models.careProfileChange.locationChangeDescription"
              values={{ name: change.resource.locationClient.fullName }}
            />
          );
        }
      } else if (change.resource.__typename === 'Medication') {
        const field = intl.formatMessage({ id: `forms.medication.${camel}Label` });

        return (
          <FormattedMessage
            id="models.careProfileChange.medicationChangeDescription"
            values={{ field, name: change.resource.name }}
          />
        );
      } else if (change.resource.__typename === 'Provider') {
        const field = intl.formatMessage({ id: `forms.provider.${camel}Label` });

        return (
          <FormattedMessage
            id="models.careProfileChange.providerChangeDescription"
            values={{ field, name: change.resource.name }}
          />
        );
      }

      return <FormattedMessage id={`forms.careProfileNew.${camel}Label`} />;
    } else if (change.kind === CareProfileChangeKindEnum.CREATION) {
      if (change.resource.__typename === 'Email') {
        return (
          <FormattedMessage
            id="models.careProfileChange.emailAddressChangeDescription"
            values={{ name: change.resource.user.fullName }}
          />
        );
      } else if (change.resource.__typename === 'PhoneNumber') {
        return (
          <FormattedMessage
            id="models.careProfileChange.phoneNumberChangeDescription"
            values={{ name: change.resource.client.fullName }}
          />
        );
      }
    }

    if (change.kind === CareProfileChangeKindEnum.CREATION) {
      return <FormattedMessage id={`models.careProfileChange.added.${change.resource.__typename}`} />;
    } else if (change.kind === CareProfileChangeKindEnum.DELETION) {
      return <FormattedMessage id={`models.careProfileChange.deleted.${change.resource.__typename}`} />;
    }
  }, [change.field, change.kind, change.resource, intl]);

  const attributeLabelId = useCallback(
    (attribute: string) => {
      const camelAttribute = snakeToCamel(attribute);

      if (change.resource.__typename === 'Diagnosis') {
        return `forms.diagnosis.${camelAttribute}Label`;
      } else if (change.resource.__typename === 'Email') {
        return `models.careProfileChange.emailAttributes.${camelAttribute}`;
      } else if (change.resource.__typename === 'Medication') {
        return `forms.medication.${camelAttribute}Label`;
      } else if (change.resource.__typename === 'PhoneNumber') {
        return `models.careProfileChange.phoneNumberAttributes.${camelAttribute}`;
      } else if (change.resource.__typename === 'Provider') {
        return `forms.provider.${camelAttribute}Label`;
      } else if (change.resource.__typename === 'LegalDocument') {
        return `forms.legalDocument.${camelAttribute}Label`;
      }
    },
    [change.resource.__typename]
  );

  const changeDescription = useMemo(() => {
    const data = JSON.parse(change.changeJson);

    if (change.kind === CareProfileChangeKindEnum.MODIFICATION) {
      return (
        <div className={styles.careProfileChangeDescriptionRow}>
          <div>
            <FormattedMessage id="models.careProfileChange.changedFrom" />
          </div>
          <div className={styles.careProfileChangeValue}>
            <CareProfileChangeModificationValue
              field={change.field as CareProfileChangeFieldEnum}
              resource={change.resource}
              value={data['from']}
            />
          </div>
          <div>
            <FormattedMessage id="models.careProfileChange.to" />
          </div>
          <div className={styles.careProfileChangeValue}>
            <CareProfileChangeModificationValue
              field={change.field as CareProfileChangeFieldEnum}
              resource={change.resource}
              value={data['to']}
            />
          </div>
        </div>
      );
    }

    const useKeys = Object.keys(data).filter(key => {
      const d = data[key];
      return d !== undefined && d !== null;
    });

    return (
      <div className={styles.careProfileChangeAddedOrDeletedAttributes}>
        {useKeys.map(key => (
          <div key={key}>
            <div className={styles.careProfileChangeAddedOrDeletedAttribute}>
              <span className={styles.careProfileChangeAddedOrDeletedAttributeName}>
                <FormattedMessage id={attributeLabelId(key)} />
              </span>
              <span className={styles.careProfileChangeValue}>
                <CareProfileChangeModificationValue
                  field={key as CareProfileChangeFieldEnum}
                  resource={change.resource}
                  value={data[key]}
                />
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }, [attributeLabelId, change.changeJson, change.field, change.kind, change.resource]);

  return (
    <div className={styles.careProfileChange}>
      <div>{fieldDescription}</div>
      <div>{changeDescription}</div>
      <div>
        <DateStamp value={change.date} />
      </div>
    </div>
  );
};

export default CareProfileChange;
